import { ToolDef, ToolAnswer, ToolContext } from '../mcp-types';
import { NavigateDeps, unsavedRefusal } from './navigate.tool';
import { screenFromUrl } from './where-am-i.tool';

export interface ProjectChoice { id: string; name: string }

export interface OpenProjectDeps extends Pick<NavigateDeps, 'unsaved' | 'settingsUnsaved'> {
  /** The projects the select-project screen would offer this builder. Rejects when they can't be loaded. */
  accessibleProjects: () => Promise<ProjectChoice[]>;
  /** Right after a load the list fails only because sign-in hasn't finished, which checking won't help. */
  stillSigningIn?: () => boolean;
  settingsOpen: () => boolean;
  closeSettings: () => void;
  /** Switches in place and waits for the new project's lists. Null when the switch failed. */
  switchTo: (id: string) => Promise<{ entities: number; pages: number; listsArrived: boolean } | null>;
  goToFirstScreen: () => Promise<boolean>;
}

const LISTED_AT_MOST = 40;

function listed(projects: ProjectChoice[]): string {
  const shown = projects.slice(0, LISTED_AT_MOST).map((p) => `${p.name} (${p.id})`).join(', ');
  return projects.length > LISTED_AT_MOST ? `${shown}, and ${projects.length - LISTED_AT_MOST} more` : shown;
}

export function makeOpenProjectTool(deps: OpenProjectDeps): ToolDef {
  return {
    name: 'open_project',
    kind: 'live',
    worksWithoutProject: true,
    description: [
      'Opens one of the builder\'s projects, by name or id, in place, with or without another project open, and lands',
      'on its Data screen. When it answers, read_project already sees the new project.',
      'It refuses a project the builder can\'t access, and that refusal lists the ones they can, so call it with the name',
      'the builder used. It won\'t leave a screen with unsaved changes; ask the builder to save or discard them first.',
    ].join(' '),
    inputSchema: {
      type: 'object',
      properties: {
        project: { type: 'string', description: 'The project\'s name or id.' },
      },
      required: ['project'],
      additionalProperties: false,
    },

    async run(input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
      const asked = input['project'] as string;

      let projects: ProjectChoice[];
      try {
        projects = await deps.accessibleProjects();
      } catch {
        if (deps.stillSigningIn?.()) return { outcome: 'refused', refusal: 'The editor is still loading the builder\'s sign-in, so nothing was opened. Try again in a moment.' };
        return { outcome: 'refused', refusal: 'Couldn\'t load the builder\'s projects, so nothing was opened. Check that they are signed in and online, then try again.' };
      }

      const byId = projects.find((p) => p.id === asked);
      const byName = projects.filter((p) => p.name.toLowerCase() === asked.toLowerCase());
      if (!byId && byName.length > 1) {
        return { outcome: 'refused', refusal: `More than one project is called '${asked}': ${listed(byName)}. Call open_project with the id of the one the builder means.` };
      }
      const project = byId ?? byName[0];
      if (!project) {
        return {
          outcome: 'refused',
          refusal: projects.length
            ? `The builder can't open '${asked}': it isn't one of their projects. They can open: ${listed(projects)}.`
            : `The builder can't open '${asked}': they have no projects yet.`,
        };
      }

      if (project.id === ctx.projectId) {
        if (screenFromUrl(ctx.routerUrl) !== 'select-project') {
          return { outcome: 'done', changed: [], result: { alreadyOpen: true, project } };
        }
        // Moving on from the picker leaves it, and a new project's name typed there would go with it.
        const onPicker = unsavedRefusal(ctx, deps);
        if (onPicker) return onPicker;
        await deps.goToFirstScreen();
        return { outcome: 'done', changed: [`Opened ${project.name}`], result: { project, screen: 'data' } };
      }

      const refusal = unsavedRefusal(ctx, deps);
      if (refusal) return refusal;

      // The overlay belongs to the project being left.
      if (deps.settingsOpen()) deps.closeSettings();

      const opened = await deps.switchTo(project.id);
      if (!opened) return { outcome: 'refused', refusal: `Couldn't open ${project.name}. Ask the builder to open it from the project list.` };

      const changed = [`Opened ${project.name}`];
      if (!opened.listsArrived) {
        return {
          outcome: 'done',
          changed,
          result: { project, screen: 'data', note: 'The project\'s lists are still loading, so read_project may come back short. Call it again in a moment.' },
        };
      }
      return { outcome: 'done', changed, result: { project, screen: 'data', entities: opened.entities, pages: opened.pages } };
    },
  };
}
